<template>
  <h1>Admin Notifications</h1>
  <Card>
    <template #content>
      <div class="centre-flex flex-row">
        <h1>Admin Notifications</h1>
      </div>
      <Divider />
      <div class="notifications-list">
        <div v-for="notification in notifications" :key="notification.id" class="notification-item">
          <div class="notification-header">
            <span class="notification-time">{{ notification.created_at }}</span>
            <span class="notification-action">{{ notification.action }}</span>
          </div>
          <div class="notification-content">
            {{ notification.message }}
          </div>
          <div class="notification-footer">
            <span class="notification-user">From: {{ notification.user_username }}</span>
            <span v-if="notification.site_name" class="notification-site">Site: {{ notification.site_name }}</span>
          </div>
          <div class="notification-actions" v-if="isAdmin">
            <Button 
              v-if="notification.action === 'delete_request'"
              @click="handleDeleteSite(notification.site)"
              class="p-button-danger"
            >
              Delete Site
            </Button>
            <Button 
              @click="markAsRead(notification)"
              :disabled="notification.is_read"
            >
              Mark as Read
            </Button>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue'
import { useAuthStore } from '@/stores/auth'

interface AdminNotification {
  id: number
  message: string
  created_at: string
  is_read: boolean
  user: number
  user_username: string
  site: number
  site_name: string
  action: string
}

export default defineComponent({
  name: 'AdminNotifications',
  setup() {
    const notifications = ref<AdminNotification[]>([])
    const authStore = useAuthStore()
    const isAdmin = ref(false)

    const fetchNotifications = async () => {
      try {
        const csrfToken = await authStore.getCsrfToken()
        const response = await fetch('/api/admin-notifications/', {
          headers: {
            'X-CSRFToken': csrfToken
          }
        })
        if (response.ok) {
          notifications.value = await response.json()
        }
      } catch (error) {
        console.error('Error fetching notifications:', error)
      }
    }

    const markAsRead = async (notification: AdminNotification) => {
      try {
        const csrfToken = await authStore.getCsrfToken()
        const response = await fetch(`/api/admin-notifications/${notification.id}/`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken
          },
          body: JSON.stringify({ is_read: true })
        })
        if (response.ok) {
          notification.is_read = true
        }
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    }

    const handleDeleteSite = async (siteId: number) => {
      if (!confirm('Are you sure you want to delete this site?')) {
        return
      }

      try {
        const csrfToken = await authStore.getCsrfToken()
        const response = await fetch(`/api/sites/${siteId}/`, {
          method: 'DELETE',
          headers: {
            'X-CSRFToken': csrfToken
          }
        })
        if (response.ok) {
          // Remove notifications related to this site
          notifications.value = notifications.value.filter(n => n.site !== siteId)
        }
      } catch (error) {
        console.error('Error deleting site:', error)
      }
    }

    onMounted(async () => {
      await fetchNotifications()
      // Check if user is admin
      const user = await authStore.get_pk()
      isAdmin.value = user?.is_superuser || false
    })

    return {
      notifications,
      isAdmin,
      markAsRead,
      handleDeleteSite
    }
  }
})
</script>

<style scoped>
.notifications-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.notification-item {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.notification-time {
  color: #666;
  font-size: 0.9rem;
}

.notification-action {
  font-weight: bold;
  text-transform: uppercase;
  color: #2196F3;
}

.notification-content {
  margin-bottom: 0.5rem;
}

.notification-footer {
  display: flex;
  gap: 1rem;
  font-size: 0.9rem;
  color: #666;
}

.notification-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}
</style> 